import { Document, isMap, isScalar, parseDocument } from 'yaml'

export interface SkillDocumentFields {
  name: string
  displayName: string
  description: string
  useWhen: string
  howToUse: string
  outputContent: string
}

const FIELD_ENTRIES: ReadonlyArray<[keyof SkillDocumentFields, string]> = [
  ['name', 'name'],
  ['displayName', 'display_name'],
  ['description', 'description'],
  ['useWhen', 'use_when'],
  ['howToUse', 'how_to_use'],
  ['outputContent', 'output_content'],
]

interface SkillDocumentParts {
  document: Document
  body: string
  rawBody: string
  hasFrontmatter: boolean
  bom: string
  lineEnding: '\n' | '\r\n' | '\r'
  openingMarker: string
  closingMarker: string
}

export class SkillDocumentYamlError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SkillDocumentYamlError'
  }
}

interface SplitFrontmatter {
  source: string
  body: string
  rawBody: string
  bom: string
  lineEnding: '\n' | '\r\n' | '\r'
  openingMarker: string
  closingMarker: string
}

function detectLineEnding(content: string): '\n' | '\r\n' | '\r' {
  const match = /\r\n|\r|\n/.exec(content)
  return match?.[0] === '\r\n' ? '\r\n' : match?.[0] === '\r' ? '\r' : '\n'
}

function splitFrontmatter(content: string): SplitFrontmatter | null {
  const bom = content.startsWith('\uFEFF') ? '\uFEFF' : ''
  const withoutBom = bom ? content.slice(1) : content
  const opening = /^---[ \t]*(?:#[^\r\n]*)?(?:\r\n|\r|\n|$)/.exec(withoutBom)
  if (!opening) return null
  const restStart = opening[0].length
  const rest = withoutBom.slice(restStart)
  const closingPattern = /^(?:---|\.\.\.)[ \t]*(?:#[^\r\n]*)?(?:\r\n|\r|\n|$)/
  let lineStart = 0
  let closing: RegExpExecArray | null = null
  while (lineStart <= rest.length) {
    closing = closingPattern.exec(rest.slice(lineStart))
    if (closing) break
    const lineEnding = /\r\n|\r|\n/.exec(rest.slice(lineStart))
    if (!lineEnding) break
    lineStart += lineEnding.index + lineEnding[0].length
  }
  if (!closing) throw new SkillDocumentYamlError('Skill frontmatter is missing its closing marker.')
  const closingStart = restStart + lineStart
  const bodyStart = closingStart + closing[0].length
  const rawBody = withoutBom.slice(bodyStart)
  return {
    source: withoutBom.slice(restStart, closingStart),
    body: rawBody.replace(/^(?:\r\n|\r|\n)/, ''),
    rawBody,
    bom,
    lineEnding: detectLineEnding(withoutBom),
    openingMarker: opening[0],
    closingMarker: closing[0],
  }
}

function parseSkillDocument(content: string): SkillDocumentParts {
  const frontmatter = splitFrontmatter(content)
  const source = frontmatter?.source ?? ''
  const document = parseDocument(source, {
    keepSourceTokens: true,
    prettyErrors: false,
    strict: true,
  })
  if (document.errors.length > 0) {
    throw new SkillDocumentYamlError(`Invalid skill YAML: ${document.errors.map((error) => error.message).join('; ')}`)
  }
  if (document.contents !== null && !isMap(document.contents)) {
    throw new SkillDocumentYamlError('Skill frontmatter must be a YAML mapping.')
  }
  return {
    document,
    body: frontmatter?.body ?? content.replace(/^\uFEFF/, ''),
    rawBody: frontmatter?.rawBody ?? content.replace(/^\uFEFF/, ''),
    hasFrontmatter: frontmatter !== null,
    bom: frontmatter?.bom ?? (content.startsWith('\uFEFF') ? '\uFEFF' : ''),
    lineEnding: frontmatter?.lineEnding ?? detectLineEnding(content),
    openingMarker: frontmatter?.openingMarker ?? '',
    closingMarker: frontmatter?.closingMarker ?? '',
  }
}

function stringField(document: Document, key: string, fallback: string): string {
  const value = document.get(key)
  return typeof value === 'string' ? value : fallback
}

function fieldsEqual(left: SkillDocumentFields, right: SkillDocumentFields): boolean {
  return FIELD_ENTRIES.every(([field]) => left[field] === right[field])
}

export function readSkillDocumentFields(content: string, fallback: SkillDocumentFields): SkillDocumentFields {
  const { document, hasFrontmatter } = parseSkillDocument(content)
  if (!hasFrontmatter) return fallback
  return {
    name: stringField(document, 'name', fallback.name),
    displayName: stringField(document, 'display_name', fallback.displayName),
    description: stringField(document, 'description', fallback.description),
    useWhen: stringField(document, 'use_when', fallback.useWhen),
    howToUse: stringField(document, 'how_to_use', fallback.howToUse),
    outputContent: stringField(document, 'output_content', fallback.outputContent),
  }
}

export function updateSkillDocumentFields(content: string, fields: SkillDocumentFields): string {
  const parts = parseSkillDocument(content)
  const current = readSkillDocumentFields(content, fields)
  if (parts.hasFrontmatter && fieldsEqual(current, fields)) return content

  for (const [field, key] of FIELD_ENTRIES) {
    const value = fields[field].trim()
    const node = parts.document.get(key, true)
    if (isScalar(node)) node.value = value
    else parts.document.set(key, value)
  }

  const serialized = parts.document.toString({ lineWidth: 0 }).replace(/\n?$/, '').replace(/\n/g, parts.lineEnding)
  if (parts.hasFrontmatter) {
    return `${parts.bom}${parts.openingMarker}${serialized}${parts.lineEnding}${parts.closingMarker}${parts.rawBody}`
  }
  return `${parts.bom}---${parts.lineEnding}${serialized}${parts.lineEnding}---${parts.lineEnding}${parts.lineEnding}${parts.body}`
}

export function isTextSkillFile(mediaType: string, path: string): boolean {
  return mediaType === 'text/markdown' || path.toLowerCase().endsWith('.md')
}

export function skillDocumentBody(content: string): string {
  return parseSkillDocument(content).body
}
